import { createContext, useContext, useEffect, useState } from "react";
import CryptoJS from "crypto-js";
import { secureStorage } from "../utils/secureStorage";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [broadcastChannel, setBroadcastChannel] = useState(null);
  const MANUAL_LOGOUT_KEY = "manual_logout_flag";
  const REMEMBER_ME_KEY = "remembered_credentials";
  const USER_DATA_KEY = "user";
  const REMEMBER_ME_EXPIRY = 365 * 24 * 60 * 60 * 1000; // 365 days
  const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;
  const API = process.env.REACT_APP_API_URL;

  // Encrypt credentials
  const encryptCredentials = (email, password) => {
    try {
      const data = JSON.stringify({ email, password });
      const encrypted = CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
      return encrypted;
    } catch (error) {
      console.error("Credentials encryption failed:", error);
      return null;
    }
  };

  // Decrypt credentials
  const decryptCredentials = (encryptedData) => {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error("Credentials decryption failed:", error);
      return null;
    }
  };

  // Get saved credentials
  const getSavedCredentials = () => {
    try {
      const saved = localStorage.getItem(REMEMBER_ME_KEY);
      if (!saved) return null;

      const data = JSON.parse(saved);
      
      // Check if expired
      if (Date.now() > data.expiry) {
        localStorage.removeItem(REMEMBER_ME_KEY);
        return null;
      }

      return decryptCredentials(data.credentials);
    } catch (error) {
      console.error("Failed to get saved credentials:", error);
      localStorage.removeItem(REMEMBER_ME_KEY);
      return null;
    }
  };

  // Save credentials
  const saveCredentials = (email, password) => {
    try {
      const encrypted = encryptCredentials(email, password);
      if (encrypted) {
        const data = {
          credentials: encrypted,
          expiry: Date.now() + REMEMBER_ME_EXPIRY
        };
        localStorage.setItem(REMEMBER_ME_KEY, JSON.stringify(data));
      }
    } catch (error) {
      console.error("Failed to save credentials:", error);
    }
  };

  // Clear saved credentials
  const clearSavedCredentials = () => {
    localStorage.removeItem(REMEMBER_ME_KEY);
  };

  // Store user data to sessionStorage
  const storeUserSession = (userData) => {
    sessionStorage.setItem("token", userData.token);
    sessionStorage.setItem("id", userData.id);
    sessionStorage.setItem("role", userData.role);
    sessionStorage.setItem("instituteType", userData.instituteType || "");
    sessionStorage.setItem("program_category_id", userData.program_category_id || "");
    sessionStorage.setItem("first_name", userData.first_name || "");
    sessionStorage.setItem("last_name", userData.last_name || "");
    sessionStorage.setItem("email", userData.email || "");
    sessionStorage.setItem("program_category", userData.program_category || "");
    sessionStorage.setItem("profile_image", userData.profile_image || "");
    sessionStorage.setItem("instituteId", userData.instituteId || "");
    sessionStorage.setItem("studentInstituteId", userData.studentInstituteId || "");
    sessionStorage.setItem("tour", userData.tour || "");
    sessionStorage.setItem("batchId", userData.batchId || "");
    sessionStorage.setItem("degree", userData.degree || "");
    sessionStorage.setItem("location", userData.location || "");
    sessionStorage.setItem("instituteLogo", userData.instituteLogo || "");
    sessionStorage.setItem("hasbatch", userData.hasbatch);
  };

  // Auto-login with saved credentials (from secureStorage)
  const attemptAutoLogin = async (isAuto = false) => {
    if (isAuto) {
      // Auto-login from secureStorage user data
      try {
        const localDataString = secureStorage.getItem(USER_DATA_KEY);
        if (!localDataString) {
          console.log('No user data in secureStorage');
          return false;
        }

        const localData = JSON.parse(localDataString);
        
        // Validate required fields
        if (!localData.token || !localData.id || !localData.role) {
          console.log('Invalid user data in secureStorage');
          secureStorage.removeItem(USER_DATA_KEY);
          return false;
        }

        console.log('Auto-login: Restoring session from secureStorage');
        
        // Store to sessionStorage
        storeUserSession(localData);

        // Update state
        setUser(localData);
        setIsAuthenticated(true);

        // Broadcast to other tabs
        if ("BroadcastChannel" in window && broadcastChannel) {
          console.log('Broadcasting AUTH_SYNC after auto-login');
          broadcastChannel.postMessage({ type: "AUTH_SYNC", data: localData });
        }

        console.log('Auto-login successful');
        return true;
      } catch (error) {
        console.error("Auto-login error:", error);
        secureStorage.removeItem(USER_DATA_KEY);
        return false;
      }
    } else {
      // Login via API with saved credentials
      try {
        const credentials = getSavedCredentials();
        if (!credentials) {
          console.log('No saved credentials found');
          return false;
        }

        console.log('Attempting API login with saved credentials...');
        
        const response = await fetch(`${API}/api/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: credentials.email,
            password: credentials.password
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          console.log('API login failed, clearing saved credentials');
          clearSavedCredentials();
          return false;
        }

        const userData = {
          token: data.result.token,
          id: data.result.id,
          role: data.result.role,
          instituteType: data.result.instituteType || "",
          program_category_id: data.result.program_category_id || "",
          first_name: data.result.first_name || "",
          last_name: data.result.last_name || "",
          email: data.result.email || "",
          program_category: data.result.program_category || "",
          profile_image: data.result.profile_image || "",
          instituteId: data.result.instituteId || "",
          studentInstituteId: data.result.studentInstituteId || "",
          tour: data.result.tour || "",
          batchId: data.result.batchId || "",
          degree:data.result.degree || "",
          location:data.result.location || "",
          instituteLogo:data.result.instituteLogo || "",
          hasbatch:data.result.hasbatch || false
        };

        // Store session data
        storeUserSession(userData);

        // Store to localStorage for auto-login
        secureStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));

        setUser(userData);
        setIsAuthenticated(true);
        console.log('API login successful:', userData.role);

        // Broadcast to other tabs
        if ("BroadcastChannel" in window && broadcastChannel) {
          broadcastChannel.postMessage({ type: "AUTH_SYNC", data: userData });
        }

        return true;
      } catch (error) {
        console.error("API login error:", error);
        clearSavedCredentials();
        return false;
      }
    }
  };

  const checkAuthStatus = () => {
    try {
      const token = sessionStorage.getItem("token");
      const id = sessionStorage.getItem("id");
      const role = sessionStorage.getItem("role");

      if (token && id && role) {
        const userData = {
          token,
          id,
          role,
          instituteType: sessionStorage.getItem("instituteType") || "",
          program_category_id: sessionStorage.getItem("program_category_id") || "",
          first_name: sessionStorage.getItem("first_name") || "",
          last_name: sessionStorage.getItem("last_name") || "",
          email: sessionStorage.getItem("email") || "",
          program_category: sessionStorage.getItem("program_category") || "",
          profile_image: sessionStorage.getItem("profile_image") || "",
          instituteId: sessionStorage.getItem("instituteId") || "",
          studentInstituteId: sessionStorage.getItem("studentInstituteId") || "",
          instituteLogo: sessionStorage.getItem("instituteLogo") || "",
          tour: sessionStorage.getItem("tour") || "",
          batchId: sessionStorage.getItem("batchId") || "",
          instituteLogo: sessionStorage.getItem("instituteLogo") || "",
          hasbatch:sessionStorage.getItem("hasbatch") || false
        };

        setUser(userData);
        setIsAuthenticated(true);
        console.log('User authenticated:', userData.role);
        return true;
      } else {
        setUser(null);
        setIsAuthenticated(false);
        console.log('User not authenticated');
        return false;
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  // BroadcastChannel setup - MUST happen before auth check
  useEffect(() => {
    if (!("BroadcastChannel" in window)) {
      console.log('BroadcastChannel not supported');
      return;
    }

    const bc = new BroadcastChannel("auth_channel");
    setBroadcastChannel(bc);
    console.log('BroadcastChannel initialized');

    const handleMessage = (event) => {
      try {
        console.log('BroadcastChannel message received:', event.data?.type);

        if (event.data?.type === "AUTH_SYNC") {
          const data = event.data.data;
          
          if (!data || !data.token || !data.id || !data.role) {
            console.error('Invalid auth data received:', data);
            return;
          }

          console.log('Syncing auth from another tab');

          // Store to sessionStorage
          storeUserSession(data);

          // Update state
          setUser(data);
          setIsAuthenticated(true);
          
        } else if (event.data?.type === "LOGOUT_SYNC") {
          console.log('Logout sync received from another tab');
          sessionStorage.clear();
          setUser(null);
          setIsAuthenticated(false);
          
        } else if (event.data?.type === "REQUEST_AUTH_SYNC") {
          const token = sessionStorage.getItem('token');
          const id = sessionStorage.getItem('id');
          const role = sessionStorage.getItem('role');
          
          if (token && id && role) {
            console.log('Responding to auth sync request');
            const authData = {
              token,
              id,
              role,
              instituteType: sessionStorage.getItem("instituteType") || "",
              program_category_id: sessionStorage.getItem("program_category_id") || "",
              first_name: sessionStorage.getItem("first_name") || "",
              last_name: sessionStorage.getItem("last_name") || "",
              email: sessionStorage.getItem("email") || "",
              program_category: sessionStorage.getItem("program_category") || "",
              profile_image: sessionStorage.getItem("profile_image") || "",
              instituteId: sessionStorage.getItem("instituteId") || "",
              studentInstituteId: sessionStorage.getItem("studentInstituteId") || "",
              instituteLogo: sessionStorage.getItem("instituteLogo") || "",
              tour: sessionStorage.getItem("tour") || "",
              batchId: sessionStorage.getItem("batchId") || "",
              instituteLogo: sessionStorage.getItem("instituteLogo") || "",
              hasbatch:sessionStorage.getItem("hasbatch") || false
            };
            
            bc.postMessage({ type: "AUTH_SYNC", data: authData });
          }
        }
      } catch (error) {
        console.error('Error handling BroadcastChannel message:', error);
      }
    };

    bc.addEventListener('message', handleMessage);

    return () => {
      console.log('Cleaning up BroadcastChannel');
      bc.removeEventListener('message', handleMessage);
      bc.close();
    };
  }, []);

  // Initial auth check with auto-login
  useEffect(() => {
    const initAuth = async () => {
      console.log('Initializing auth...');

      // First check if already authenticated via sessionStorage
      const hasSession = checkAuthStatus();
      
      if (hasSession) {
        console.log('Session found, user already authenticated');
        setIsLoading(false);
        return;
      }

      // Check if user manually logged out
      const manualLogout = localStorage.getItem(MANUAL_LOGOUT_KEY);
      
      if (manualLogout === "true") {
        console.log('Manual logout detected, skipping auto-login');
        localStorage.removeItem(MANUAL_LOGOUT_KEY);
        setIsLoading(false);
        return;
      }

      // Request auth from other tabs
      if ("BroadcastChannel" in window && broadcastChannel) {
        console.log('Requesting auth sync from other tabs');
        broadcastChannel.postMessage({ type: "REQUEST_AUTH_SYNC" });
        
        // Wait a bit for response
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Check again after potential sync
        const hasSyncedSession = checkAuthStatus();
        if (hasSyncedSession) {
          console.log('Auth synced from another tab');
          setIsLoading(false);
          return;
        }
      }

      // Try auto-login with saved user data from localStorage
      const hasLocalData = secureStorage.getItem(USER_DATA_KEY);
      
      if (hasLocalData) {
        console.log('Found saved user data, attempting auto-login...');
        const success = await attemptAutoLogin(true);
        
        if (!success) {
          console.log('Auto-login failed');
        }
      } else {
        console.log('No saved user data found');
      }

      setIsLoading(false);
    };

    // Small delay to ensure BroadcastChannel is set up
    const timer = setTimeout(() => {
      initAuth();
    }, 100);

    return () => clearTimeout(timer);
  }, [broadcastChannel]);

  const login = (token, userData, rememberMe = false, password = null) => {
    try {
      console.log('Login called with rememberMe:', rememberMe);

      const fullData = {
        token,
        ...userData,
      };

      // Store to sessionStorage
      storeUserSession(fullData);

      // Store to localStorage for auto-login
      if(rememberMe){
        secureStorage.setItem(USER_DATA_KEY, JSON.stringify(fullData));
      }

      // Clear manual logout flag on successful login
      secureStorage.removeItem(MANUAL_LOGOUT_KEY);

      // Save credentials if Remember Me is checked
      if (rememberMe && userData.email && password) {
        console.log('Saving credentials for remember me');
        saveCredentials(userData.email, password);
      } else if (!rememberMe) {
        clearSavedCredentials();
      }

      // Update state
      setUser(fullData);
      setIsAuthenticated(true);

      // Broadcast to other tabs
      if ("BroadcastChannel" in window && broadcastChannel) {
        console.log('Broadcasting AUTH_SYNC after login');
        broadcastChannel.postMessage({ type: "AUTH_SYNC", data: fullData });
      }

      console.log('Login successful');
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const logout = () => {
    try {
      console.log('Logout called');

      // Set manual logout flag to prevent auto-login
      secureStorage.setItem(MANUAL_LOGOUT_KEY, "true");
      
      // Clear saved credentials and user data
      clearSavedCredentials();
      secureStorage.removeItem(USER_DATA_KEY);
      
      // Clear session
      sessionStorage.clear();
      setUser(null);
      setIsAuthenticated(false);

      // Broadcast to other tabs
      if ("BroadcastChannel" in window && broadcastChannel) {
        console.log('Broadcasting LOGOUT_SYNC');
        broadcastChannel.postMessage({ type: "LOGOUT_SYNC" });
      }

      console.log('Logout successful');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    login,
    user,
    logout,
    checkAuthStatus,
    getSavedCredentials,
    clearSavedCredentials
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
